import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo, useState } from 'react';
import { NAVY, RADIUS_ITEM, SURFACE_TINT } from 'styles/tokens';

import { useGetAdminBusinessUnitsQuery } from '../../../services/api/adminBusinessUnits.api';
import type { Integration } from '../../../services/api/integrations.api';
import IntegrationCard from './components/IntegrationCard';
import { getStatusLabel } from './utils';

interface StatusOverviewProps {
  integrations: Integration[];
  onIntegrationClick?: (integrationId: number) => void;
}

// Tokenized filter control: no 1px border at rest, subtle navy focus ring.
const filterControlSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE_TINT,
    borderRadius: RADIUS_ITEM,
    fontFamily: 'Jost, sans-serif',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: `1px solid ${alpha(NAVY, 0.15)}` },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Jost, sans-serif' },
};

interface FilterableIntegrationCardProps {
  integration: Integration;
  statusFilter: 'all' | 'operational' | 'degraded' | 'major-issues' | 'no-data';
  selectedBusinessUnitId: number | null;
  onClick?: () => void;
}

const FilterableIntegrationCard: React.FC<FilterableIntegrationCardProps> = ({
  integration,
  statusFilter,
  selectedBusinessUnitId,
  onClick,
}) => {
  // Use pre-computed stats for filtering (business-unit-specific if selected)
  const successRate = useMemo(() => {
    if (selectedBusinessUnitId && integration.last24HourStats?.statsByBusinessUnit?.[selectedBusinessUnitId]) {
      return integration.last24HourStats.statsByBusinessUnit[selectedBusinessUnitId].successRate ?? null;
    }
    return integration.last24HourStats?.successRate ?? null;
  }, [selectedBusinessUnitId, integration.last24HourStats]);

  const statusLabel = getStatusLabel(successRate);

  const shouldDisplay = useMemo(() => {
    if (statusFilter === 'all') return true;

    switch (statusFilter) {
      case 'operational':
        return statusLabel === 'Operational';
      case 'degraded':
        return statusLabel === 'Degraded';
      case 'major-issues':
        return statusLabel === 'Major Issues';
      case 'no-data':
        return statusLabel === 'No Data';
      default:
        return true;
    }
  }, [statusLabel, statusFilter]);

  if (!shouldDisplay) return null;

  return <IntegrationCard integration={integration} onClick={onClick} businessUnitId={selectedBusinessUnitId} />;
};

const StatusOverview: React.FC<StatusOverviewProps> = ({ integrations, onIntegrationClick }) => {
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'operational' | 'degraded' | 'major-issues' | 'no-data'>(
    'all'
  );

  // Fetch business units to get names
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(undefined);

  // Get all unique business unit IDs from all integrations
  const allBusinessUnitIds = useMemo(() => {
    const ids = new Set<number>();
    integrations.forEach((integration) => {
      integration.businessUnitIds.forEach((id) => ids.add(id));
    });
    return Array.from(ids);
  }, [integrations]);

  // Get available business units (only those that have integrations)
  const availableBusinessUnits = useMemo(() => {
    if (!businessUnits) return [];
    return businessUnits.filter((bu) => allBusinessUnitIds.includes(bu.id));
  }, [businessUnits, allBusinessUnitIds]);

  const filteredIntegrations = useMemo(() => {
    if (selectedBusinessUnitId === null) {
      return integrations;
    }
    return integrations.filter((integration) => integration.businessUnitIds.includes(selectedBusinessUnitId));
  }, [integrations, selectedBusinessUnitId]);

  return (
    <Box>
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Business Unit Filter */}
        {availableBusinessUnits.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 250, ...filterControlSx }}>
            <InputLabel>Business Unit</InputLabel>
            <Select
              value={selectedBusinessUnitId || ''}
              onChange={(e) => {
                setSelectedBusinessUnitId(String(e.target.value) === '' ? null : Number(e.target.value));
              }}
              label="Business Unit"
              sx={{ height: '100%' }}
            >
              <MenuItem value="">Todas</MenuItem>
              {availableBusinessUnits.map((bu) => (
                <MenuItem key={bu.id} value={bu.id}>
                  {bu.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 200, ...filterControlSx }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'operational' | 'degraded' | 'major-issues' | 'no-data');
            }}
            label="Status"
            sx={{ height: '100%' }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="operational">Operational</MenuItem>
            <MenuItem value="degraded">Degraded</MenuItem>
            <MenuItem value="major-issues">Major Issues</MenuItem>
            <MenuItem value="no-data">No Data</MenuItem>
          </Select>
        </FormControl>

        {/* Total Integrations Count */}
        <Typography
          variant="body2"
          sx={{
            color: alpha(NAVY, 0.6),
            fontFamily: 'Jost, sans-serif',
            ml: 'auto',
          }}
        >
          {filteredIntegrations.length === integrations.length
            ? `Total de integraciones: ${integrations.length}`
            : `Total de integraciones: ${filteredIntegrations.length} de ${integrations.length}`}
        </Typography>
      </Box>
      {filteredIntegrations.length === 0 && (
        <Typography
          variant="body1"
          sx={{
            color: alpha(NAVY, 0.6),
            fontFamily: 'Jost, sans-serif',
          }}
        >
          No se encontraron integraciones. Las integraciones aparecerán aquí una vez que se registren ejecuciones.
        </Typography>
      )}
      {filteredIntegrations.map((integration) => (
        <FilterableIntegrationCard
          key={integration.id}
          integration={integration}
          statusFilter={statusFilter}
          selectedBusinessUnitId={selectedBusinessUnitId}
          onClick={() => onIntegrationClick?.(integration.id)}
        />
      ))}
    </Box>
  );
};

export default StatusOverview;
